import { type UIEvent, useCallback } from 'react';
import type {
  GetProjectsForWorkspacesListQueryVariables,
  ProjectOrderInput,
  ProjectStatusInput,
} from '@frameio/graphql-client/generated';
import { useGetProjectsForWorkspacesListQuery } from '@frameio/graphql-client/generated';

const PAGE_COUNT = 100;

type UsePaginatedGetProjectsForWorkspacesListProps = {
  accountId: string;
  sortBy: ProjectOrderInput;
  filters: ProjectStatusInput[];
};

export const usePaginatedGetProjectsForWorkspacesList = ({
  accountId,
  sortBy,
  filters = ['ACTIVE', 'INACTIVE'],
}: UsePaginatedGetProjectsForWorkspacesListProps) => {
  const { data, loading, fetchMore, error, refetch } =
    useGetProjectsForWorkspacesListQuery({
      variables: {
        accountId,
        projectsPage: {
          first: PAGE_COUNT,
        },
        fullAccessProjectsPage: {
          first: PAGE_COUNT,
        },
        partialAccessProjectsPage: {
          first: PAGE_COUNT,
        },
        filters,
        sortBy,
      },
      skip: !accountId,
    });

  const { projects, fullAccessProjects, partialAccessProjects } =
    data?.account || {};

  const fetchNextPage = useCallback(() => {
    const hasNextPage =
      projects?.pageInfo?.hasNextPage ||
      fullAccessProjects?.pageInfo?.hasNextPage ||
      partialAccessProjects?.pageInfo?.hasNextPage;
    if (!hasNextPage || loading) {
      return;
    }

    const newPageVariables: Partial<
      Pick<
        GetProjectsForWorkspacesListQueryVariables,
        'projectsPage' | 'fullAccessProjectsPage' | 'partialAccessProjectsPage'
      >
    > = {
      projectsPage: {
        first: projects?.pageInfo?.hasNextPage ? PAGE_COUNT : 0,
        after: projects?.pageInfo?.endCursor,
      },
      fullAccessProjectsPage: {
        first: fullAccessProjects?.pageInfo?.hasNextPage ? PAGE_COUNT : 0,
        after: fullAccessProjects?.pageInfo?.endCursor,
      },
      partialAccessProjectsPage: {
        first: partialAccessProjects?.pageInfo?.hasNextPage ? PAGE_COUNT : 0,
        after: partialAccessProjects?.pageInfo?.endCursor,
      },
    };

    fetchMore({ variables: newPageVariables });
  }, [fetchMore, loading, projects, fullAccessProjects, partialAccessProjects]);

  const handleFetchNextProjectsOnScroll = useCallback(
    async (event: UIEvent<HTMLElement>) => {
      const scrollTop = event.currentTarget?.scrollTop;

      const scrollPosition = event.currentTarget?.offsetHeight + scrollTop;
      const fetchBoundary = event.currentTarget?.scrollHeight * 0.5;
      const hasScrolledPastFetchBoundary = scrollPosition >= fetchBoundary;

      if (hasScrolledPastFetchBoundary) {
        fetchNextPage();
      }
    },
    [fetchNextPage]
  );

  return {
    data,
    loading,
    error,
    fetchNextPage,
    handleFetchNextProjectsOnScroll,
    refetch,
  };
};
